import { DASHBOARD_ROUTES } from "./dashboardRoutes";
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./CurrentlyStudyingSlider.css";
import MultipleItemSlider from "../components/MultipleItemSlider";
import { useExamsByCategory } from "../hooks/useExamsByCategory";
import { useIsExamSelectionPending, useSetUserExamCategory } from "../hooks/useSetUserExamCategory";
import { uniqueStudyingExams } from "./currentlyStudyingExams";

const currentlyStudyingSettings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: false,
  pauseOnHover: true,
  arrows: true,
  swipeToSlide: false,
  focusOnSelect: false,
  draggable: true,
  touchMove: true,
  touchThreshold: 15,
  responsive: [
    {
      breakpoint: 1920,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 1600,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 2,
        arrows: false,
      },
    },
      {
      breakpoint: 576,
      settings: {
        slidesToShow: 1.7,
        arrows: false,
      },
    },
      {
      breakpoint: 400,
      settings: {
        slidesToShow: 1,
        arrows: false,
      },
    },
  ],
};

const StudyingCard = ({ card, isSaving, onSelect }) => {
  return (
    <button
      type="button"
      className={`cs-card cs-card--button${card.isDefault ? " is-selected" : ""}`}
      disabled={isSaving}
      onClick={() => onSelect(card)}
    >
      <div className="cs-card__top">
        <div className="cs-card__logoWrap">
          <img
            className="cs-card__logo"
            src={card.logo || undefined}
            alt={`${card.title} logo`}
            draggable={false}
          />
        </div>

        <div className="cs-card__text">
          <h3 className="cs-card__title" title={card.title}>{card.title}</h3>
          <p className="cs-card__subtitle" title={card.subtitle}>{card.subtitle}</p>
        </div>

        {card.isDefault ? (
          <span className="cs-card__check" aria-hidden="true">
            <i className="bi bi-check-lg" />
          </span>
        ) : null}
      </div>
    </button>
  );
};

const mapExamToCard = (exam, fallbackImage) => ({
  id: Number(exam.md_exams_id),
  title: exam.exam_name || "Exam",
  subtitle: exam.state_name || "All India",
  logo: exam.exam_image || fallbackImage || "",
  isDefault: Number(exam.default_exam) === 1,
});

const CurrentlyStudyingSlider = ({ categoryId }) => {
  const gesture = useRef(null);
  const startGesture = (event) => {
    gesture.current = { x: event.clientX, y: event.clientY, moved: false };
  };
  const trackGesture = (event) => {
    const current = gesture.current;
    if (current && Math.hypot(event.clientX - current.x, event.clientY - current.y) > 5) {
      current.moved = true;
    }
  };
  const preventDragSelection = (event) => {
    if (gesture.current?.moved) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const [loadError, setLoadError] = useState("");
  const {
    data: examsData,
    isLoading,
    isError,
    error,
  } = useExamsByCategory(categoryId, { isHome: true });
  const setUserExamCategoryMutation = useSetUserExamCategory();
  const isSaving = useIsExamSelectionPending();
  const studyingCards = useMemo(() => {
    const examsList = Array.isArray(examsData?.examsList) ? examsData.examsList : [];
    return uniqueStudyingExams(examsList).map((exam) => mapExamToCard(exam, examsData?.categoryImage));
  }, [examsData]);
  const sliderSettings = useMemo(() => ({
    ...currentlyStudyingSettings,
    slidesToShow: Math.min(currentlyStudyingSettings.slidesToShow, studyingCards.length),
    responsive: currentlyStudyingSettings.responsive.map(({ breakpoint, settings }) => ({
      breakpoint,
      settings: {
        ...settings,
        slidesToShow: Math.min(settings.slidesToShow, studyingCards.length),
      },
    })),
  }), [studyingCards.length]);

  const handleExamSelect = async (card) => {
    if (!categoryId || !card?.id || card.isDefault || isSaving) {
      return;
    }
    setLoadError("");

    try {
      await setUserExamCategoryMutation.mutateAsync({
        exam_category_id: categoryId,
        md_exam_id: card.id,
      });
    } catch (error) {
      setLoadError(error.message || "Unable to update the selected exam.");
    }
  };

  const resolvedLoadError = loadError || (isError
    ? error?.response?.data?.message || error?.message || "Unable to load exams."
    : "");
  const shouldShowInitialLoader = isLoading && !studyingCards.length;
  const shouldShowEmptyState = !shouldShowInitialLoader && !resolvedLoadError && !studyingCards.length;

  return (
    <section className={`cs-section${studyingCards.length <= 3 ? " cs-section--compact" : ""}`} aria-label="Currently studying">
      <div className="cs-slider-wrapper"
        onPointerDownCapture={startGesture}
        onPointerMoveCapture={trackGesture}
        onPointerUpCapture={trackGesture}
        onPointerCancelCapture={() => { if (gesture.current) gesture.current.moved = true; }}
        onClickCapture={preventDragSelection}
        onKeyDownCapture={(event) => {
          if (event.key === 'Enter' || event.key === ' ') gesture.current = null;
        }}
      >
        {shouldShowInitialLoader ? <div className="cs-status">Loading exams...</div> : null}
        {resolvedLoadError && !studyingCards.length ? <div className="cs-status">{resolvedLoadError}</div> : null}
        {shouldShowEmptyState ? <div className="cs-status">No exams available.</div> : null}

        {studyingCards.length ? (
          <>
            {studyingCards.length <= 3 ? (
              <div className="cs-static-list">
                {studyingCards.map((card, index) => (
                  <StudyingCard
                    key={`${card.id}-${index}`}
                    card={card}
                    isSaving={isSaving}
                    onSelect={handleExamSelect}
                  />
                ))}
              </div>
            ) : (
              <MultipleItemSlider
                key={categoryId}
                items={studyingCards}
                settings={sliderSettings}
                className="currently-studying-slider"
                renderItem={(card) => (
                  <StudyingCard
                    card={card}
                    isSaving={isSaving}
                    onSelect={handleExamSelect}
                  />
                )}
              />
            )}
            {!isSaving && resolvedLoadError ? <div className="cs-saveState">{resolvedLoadError}</div> : null}
          </>
        ) : null}
      </div>

      <Link
        to={DASHBOARD_ROUTES.examCategories}
        className="cs-addExam"
        aria-label="Add exam"
      >
        <span className="cs-addExam__icon" aria-hidden="true">
          <i className="bi bi-plus" />
        </span>

        <span className="cs-addExam__title">Add Exam</span>
      </Link>
    </section>
  );
};

export default CurrentlyStudyingSlider;
